// Auth context — JWT stored in sessionStorage to reduce XSS-persistence risk.
// sessionStorage is scoped to a single browser tab and cleared on tab close, which
// is a pragmatic middle ground vs. httpOnly cookies (which would require a broader
// backend refactor and CORS-with-credentials strategy).
//
// NOTE: sessionStorage is still readable by any script running on the same origin,
//       so XSS protection (CSP, input sanitization) remains the primary defence.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "./api";

const AUTH_KEY = "scentodore_auth_v1";
const AuthContext = createContext(null);

function readAuthFromStorage() {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (raw) return JSON.parse(raw);
    // Backward compat: migrate any pre-existing localStorage value once.
    const legacy = localStorage.getItem(AUTH_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      sessionStorage.setItem(AUTH_KEY, legacy);
      localStorage.removeItem(AUTH_KEY);
      return parsed;
    }
  } catch (err) {
    // Corrupted storage — reset silently but log for observability.
    // eslint-disable-next-line no-console
    console.warn("[auth] failed to read auth state from storage", err);
  }
  return { token: null, user: null };
}

function persistAuthToStorage(state) {
  try {
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(state));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[auth] failed to persist auth state", err);
  }
}

export function AuthProvider({ children }) {
  const [state, setState] = useState(readAuthFromStorage);
  const [loading, setLoading] = useState(false);
  // Track the token value in a ref so setState updates in callbacks don't
  // need `state.token` in their dependency array (which caused stale-closure
  // warnings previously).
  const tokenRef = useRef(state.token);

  // Attach token to axios instance + persist
  useEffect(() => {
    tokenRef.current = state.token;
    api.defaults.headers.common.Authorization = state.token
      ? `Bearer ${state.token}`
      : "";
    persistAuthToStorage(state);
  }, [state]);

  // On mount, if we have a token, refresh /auth/me
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (!tokenRef.current) return;
    api
      .get("/auth/me")
      .then((res) =>
        setState((s) => ({ ...s, user: { ...s.user, ...res.data } })),
      )
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[auth] /auth/me failed; clearing session", err?.response?.status);
        setState({ token: null, user: null });
      });
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setState({ token: data.access_token, user: data.user });
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email, password, full_name) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, full_name });
      setState({ token: data.access_token, user: data.user });
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginAsDemo = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/demo");
      setState({ token: data.access_token, user: data.user });
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setState({ token: null, user: null });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!tokenRef.current) return null;
    try {
      const { data } = await api.get("/auth/me");
      setState((s) => ({ ...s, user: { ...s.user, ...data } }));
      return data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] refreshUser failed", err?.response?.status);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      token: state.token,
      user: state.user,
      loading,
      isAuthenticated: !!state.token,
      login,
      register,
      loginAsDemo,
      logout,
      refreshUser,
    }),
    [state, loading, login, register, loginAsDemo, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
